<script setup lang="ts">
import { useImage } from '@vueuse/core';
import { watch } from 'vue';

import { trackImageLoad } from '@libs/feature-toggle';

import type { AppImgType } from './AppImg.model';

interface AppImgProps {
  src: string;
  imgType?: AppImgType;
  alt?: string;
  trackTime?: boolean;
}

interface AppImgEmit {
  (eventName: 'img-load-error', src: string): void;
  (eventName: 'click'): void;
}

const props = withDefaults(defineProps<AppImgProps>(), {
  imgType: 'cover',
  trackTime: false,
});
const emit = defineEmits<AppImgEmit>();

const startTime = Date.now();

const { isLoading, error } = useImage({ src: props.src });

if (props.trackTime) {
  watch(isLoading, () => {
    if (!isLoading.value) {
      trackImageLoad({
        loadTime: Date.now() - startTime,
      });
    }
  });
}

watch(error, () => {
  if (props.trackTime) {
    trackImageLoad({
      loadTime: Date.now() - startTime,
      error: true,
    });
  }

  emit('img-load-error', props.src);
});
</script>

<template>
  <img
    v-if="!isLoading"
    :src="props.src"
    :alt="props.alt"
    :class="{
      img: true,
      [props.imgType]: props.imgType,
    }"
    @click="emit('click')"
  />
  <div
    v-else
    :class="{
      img: true,
      [props.imgType]: props.imgType,
    }"
  />
</template>

<style scoped lang="scss">
.img {
  &.cover {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }

  &.contain {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }

  &.scaleX {
    height: 100%;
    width: auto;
  }
}
</style>
